import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import update from "immutability-helper";
import { Workout } from "./workoutsColumns";
import {
  DROP_ANIMATION_DURATION_MS,
  RecentDropState,
  WorkoutDragData,
  WorkoutDragOverlayContent,
  WORKOUT_DROP_ANIMATION,
  insertLibraryExerciseAtDropZone,
  isWorkoutDragData,
  isWorkoutDropZoneData,
  moveExerciseToGroupIndex,
  moveExerciseToSupersetPosition,
  moveGroupToIndex,
  workoutCollisionDetection,
} from "./workoutEditorDnd";

type WorkoutEditorWorkspaceContextValue = {
  activeDrag: WorkoutDragData | null;
  recentDrop: RecentDropState | null;
};

const WorkoutEditorWorkspaceContext =
  createContext<WorkoutEditorWorkspaceContextValue>({
    activeDrag: null,
    recentDrop: null,
  });

export const useWorkoutEditorWorkspace = () =>
  useContext(WorkoutEditorWorkspaceContext);

interface WorkoutEditorWorkspaceProps {
  workout: Workout;
  updateWorkout: (workout: Workout) => void;
  setHasChanges: (hasChanges: boolean) => void;
  children: ReactNode;
  className?: string;
}

const WorkoutEditorWorkspace = ({
  workout,
  updateWorkout,
  setHasChanges,
  children,
  className,
}: WorkoutEditorWorkspaceProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
  );
  const [activeDrag, setActiveDrag] = useState<WorkoutDragData | null>(null);
  const [recentDrop, setRecentDrop] = useState<RecentDropState | null>(null);

  useEffect(() => {
    if (!recentDrop) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setRecentDrop(null);
    }, DROP_ANIMATION_DURATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [recentDrop]);

  const clearDragState = useCallback(() => {
    document.body.style.removeProperty("cursor");
    setActiveDrag(null);
  }, []);

  return (
    <WorkoutEditorWorkspaceContext.Provider value={{ activeDrag, recentDrop }}>
      <DndContext
        sensors={sensors}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        collisionDetection={workoutCollisionDetection}
        autoScroll={{
          enabled: true,
          canScroll: (element) => {
            if (activeDrag?.type !== "library-exercise") {
              return true;
            }

            return !(
              element instanceof HTMLElement &&
              element.closest("[data-workout-library-scroll='true']")
            );
          },
        }}
        onDragStart={(event) => {
          const dragData = isWorkoutDragData(event.active.data.current)
            ? event.active.data.current
            : null;

          if (!dragData) {
            return;
          }

          setActiveDrag(dragData);
          document.body.style.setProperty("cursor", "grabbing", "important");
        }}
        onDragEnd={(event) => {
          const dragData = isWorkoutDragData(event.active.data.current)
            ? event.active.data.current
            : null;
          const dropZoneData = isWorkoutDropZoneData(event.over?.data.current)
            ? event.over?.data.current
            : null;

          clearDragState();

          if (!dragData || !dropZoneData) {
            return;
          }

          const result =
            dragData.type === "group"
              ? dropZoneData.type === "group-insert"
                ? moveGroupToIndex(
                    workout.exerciseCollection,
                    dragData,
                    dropZoneData,
                  )
                : null
              : dragData.type === "exercise"
                ? dropZoneData.type === "group-insert"
                  ? moveExerciseToGroupIndex(
                      workout.exerciseCollection,
                      dragData,
                      dropZoneData,
                    )
                  : moveExerciseToSupersetPosition(
                      workout.exerciseCollection,
                      dragData,
                      dropZoneData,
                    )
                : insertLibraryExerciseAtDropZone(
                    workout.exerciseCollection,
                    dragData,
                    dropZoneData,
                  );

          if (!result) {
            return;
          }

          updateWorkout(
            update(workout, {
              exerciseCollection: { $set: result.exerciseCollection },
            }),
          );
          setRecentDrop(result.recentDrop);
          setHasChanges(true);
        }}
        onDragCancel={() => {
          clearDragState();
        }}
      >
        <div className={className}>{children}</div>

        <DragOverlay
          modifiers={[snapCenterToCursor]}
          dropAnimation={WORKOUT_DROP_ANIMATION}
        >
          <WorkoutDragOverlayContent activeDrag={activeDrag} />
        </DragOverlay>
      </DndContext>
    </WorkoutEditorWorkspaceContext.Provider>
  );
};

export default WorkoutEditorWorkspace;
